import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, QrCode, Store, Tag } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import QRCode from 'qrcode';
import { useAuth } from '../context/AuthProvider.tsx';
import { getComercioBySlug, getProductosMarket, type ComercioStore, type ProductoStore } from '../services/marketplaceService.tsx';
import { generarQrDinamico, getComerciosResumen } from '../services/puntosAdminService.tsx';
import LogoCamara from '../assets/LogoCamara.png';

type AdminSocio = { socio_id: number; nombre?: string; mail?: string | null };
type ComercioResumen = {
  comercio_id: number;
  nombre: string;
  activo: boolean;
  admins?: AdminSocio[];
};

type ComercioDescripcionMeta = {
  texto?: string;
  direccion?: string;
  telefono?: string;
};

const norm = (v: string) =>
  String(v || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const toCleanString = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const parseDescripcionMeta = (descripcion?: string): ComercioDescripcionMeta => {
  const fallback = toCleanString(descripcion);
  if (!fallback) return {};

  try {
    const parsed = JSON.parse(fallback);
    if (!parsed || typeof parsed !== 'object') return { texto: fallback };

    const obj = parsed as any;
    const texto = toCleanString(obj.bio || obj.descripcion || obj.detalle || obj.resumen);
    const direccion = toCleanString(obj.direccion || obj.address || obj.ubicacion?.direccion);
    const telefono = toCleanString(
      obj.telefono || obj.whatsapp || obj.celular || obj.contacto?.telefono || obj.contacto?.celular
    );

    return { texto, direccion, telefono };
  } catch {
    return { texto: fallback };
  }
};

export default function StorePageMvp() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [comercio, setComercio] = useState<ComercioStore | null>(null);
  const [productos, setProductos] = useState<ProductoStore[]>([]);

  const [comercioPuntosId, setComercioPuntosId] = useState<number | null>(null);
  const [qrActivo, setQrActivo] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qrPayload, setQrPayload] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [generandoQr, setGenerandoQr] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const run = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        const c = await getComercioBySlug(slug);
        setComercio(c);
        const p = await getProductosMarket(Number(c.comercio_id));
        setProductos(p || []);
      } catch {
        setComercio(null);
        setProductos([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [slug]);

  useEffect(() => {
    const loadQrAccess = async () => {
      if (!comercio || !isAuthenticated) return;
      try {
        const token = await getAccessTokenSilently();
        const resumen = (await getComerciosResumen(token)) as ComercioResumen[];
        const match = (resumen || []).find((r) => norm(r.nombre) === norm(comercio.nombre));
        if (!match?.comercio_id) {
          setComercioPuntosId(null);
          setQrActivo(false);
          return;
        }

        const socioSel = localStorage.getItem('socioSeleccionado');
        const socioParsed = socioSel ? JSON.parse(socioSel) : null;
        const sid = Number(socioParsed?.socio_id);
        const esAdminDelComercio = Array.isArray(match.admins)
          ? match.admins.some((a) => Number(a?.socio_id) === sid)
          : false;

        if (isAdmin || esAdminDelComercio) {
          setComercioPuntosId(Number(match.comercio_id));
          setQrActivo(Boolean(match.activo));
        } else {
          setComercioPuntosId(null);
          setQrActivo(false);
        }
      } catch {
        setComercioPuntosId(null);
        setQrActivo(false);
      }
    };

    loadQrAccess();
  }, [comercio, isAuthenticated, getAccessTokenSilently, isAdmin]);

  useEffect(() => {
    const run = async () => {
      if (!qrPayload) return setQrDataUrl('');
      setQrDataUrl(await QRCode.toDataURL(qrPayload, { width: 700, margin: 1 }));
    };
    run();
  }, [qrPayload]);

  useEffect(() => {
    if (!showQr || !qrActivo || !expiresAt) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      const remaining = expiresAt - Date.now();
      if (remaining <= 0 && !generandoQr) {
        generarQr();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [showQr, qrActivo, expiresAt, generandoQr]);

  const segundos = useMemo(() => (expiresAt ? Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)) : 0), [expiresAt, tick]);

  const comercioInfo = useMemo(() => {
    if (!comercio) return { texto: '', direccion: '', telefono: '' };

    const metaDescripcion = parseDescripcionMeta(comercio.descripcion);
    const emprendedor: any = (comercio as any)?.emprendedor || {};

    return {
      texto:
        toCleanString(metaDescripcion.texto) ||
        toCleanString(emprendedor?.bio) ||
        'Catálogo de productos. La compra se coordina directamente con la tienda.',
      direccion: toCleanString(metaDescripcion.direccion) || toCleanString(emprendedor?.direccion),
      telefono:
        toCleanString(metaDescripcion.telefono) ||
        toCleanString(emprendedor?.telefono) ||
        toCleanString(emprendedor?.whatsapp),
    };
  }, [comercio]);

  const generarQr = async () => {
    if (!comercioPuntosId || !qrActivo) return;
    try {
      setGenerandoQr(true);
      const token = await getAccessTokenSilently();
      const resp = await generarQrDinamico(comercioPuntosId, token, 60);
      setQrPayload(resp.token_qr || '');
      setExpiresAt(resp.expires_at ? new Date(resp.expires_at).getTime() : null);
    } finally {
      setGenerandoQr(false);
    }
  };

  const abrirQr = async () => {
    setShowQr(true);
    setQrPayload('');
    setExpiresAt(null);
    if (qrActivo) await generarQr();
  };

  if (loading) {
    return <div className="min-h-screen bg-[#eef6ff] p-6"><div className="mx-auto max-w-6xl rounded-xl bg-white p-6">Cargando tienda...</div></div>;
  }

  if (!comercio) {
    return (
      <div className="min-h-screen bg-[#eef6ff] p-6">
        <div className="mx-auto max-w-6xl rounded-xl bg-white p-6 text-center">
          <p className="text-lg font-bold text-slate-800">Tienda no encontrada</p>
          <button className="mt-3 rounded bg-sky-600 px-4 py-2 text-white" onClick={() => navigate('/catalogo')}>Volver al marketplace</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef6ff]">
      <header className="sticky top-0 z-20 border-b border-sky-200 bg-white/95 backdrop-blur">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-3 px-4 py-3 md:grid-cols-[320px_1fr_220px] sm:px-6">
          <button onClick={() => navigate('/catalogo')} className="flex items-center gap-2 text-left">
            <img src={LogoCamara} alt="Camara Comercial Bolívar" className="h-16 w-16 rounded-lg border border-sky-100 bg-white object-contain p-1" />
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-sky-600">Marketplace Socios</p>
              <h1 className="text-sm font-bold text-slate-800 sm:text-base">Cámara Comercial de Bolívar</h1>
            </div>
          </button>

          <div className="hidden text-sm font-semibold text-slate-600 md:block">Tienda oficial</div>

          <div className="ml-auto flex items-center justify-end gap-2">
            <span className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 sm:text-sm">
              Catálogo informativo
            </span>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-7xl gap-2 overflow-x-auto px-4 pb-3 sm:px-6">
          {["Ofertas", "Tecnología", "Hogar", "Gastronomía", "Servicios", "Industria local"].map((cat) => (
            <span key={cat} className="whitespace-nowrap rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold text-sky-700">
              {cat}
            </span>
          ))}
        </div>
      </header>

      <div className="mx-auto max-w-6xl p-4 sm:p-6">
        <button onClick={() => navigate('/catalogo')} className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-sky-700">
          <ArrowLeft className="h-4 w-4" /> Volver al marketplace
        </button>

        <div className="mb-4 rounded-2xl border border-sky-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            {comercio.logo_url ? (
              <img src={comercio.logo_url} alt={comercio.nombre} className="h-16 w-16 rounded-xl border border-sky-100 object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-sky-50"><Store className="h-7 w-7 text-sky-400" /></div>
            )}
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900">{comercio.nombre}</h1>
              <div className="mt-1">
                {comercio.es_emprendedor || comercio.emprendedor?.tipo === 'emprendedor' ? (
                  <span className="inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">Emprendedor local</span>
                ) : (
                  <span className="inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700">Tienda oficial</span>
                )}
              </div>
              <p className="text-sm text-slate-600">{comercioInfo.texto}</p>
              {(comercioInfo.direccion || comercioInfo.telefono) && (
                <div className="mt-1 space-y-0.5 text-xs text-slate-600">
                  {comercioInfo.direccion && <p><b>Dirección:</b> {comercioInfo.direccion}</p>}
                  {comercioInfo.telefono && <p><b>Teléfono:</b> {comercioInfo.telefono}</p>}
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {productos.map((p) => (
            <article key={p.producto_id} className="rounded-2xl border border-sky-100 bg-white p-3 shadow-sm">
              <div className="mb-3 flex h-40 items-center justify-center rounded-xl bg-sky-50">
                {p.imagen_url ? <img src={p.imagen_url} alt={p.nombre} className="h-full w-full rounded-xl object-cover" /> : <Store className="h-12 w-12 text-sky-300" />}
              </div>
              <h4 className="text-sm font-semibold text-slate-800">{p.nombre}</h4>
              <p className="mt-2 text-2xl font-extrabold text-slate-900">$ {Number(p.precio || 0).toLocaleString('es-AR')}</p>
              <p className="text-xs text-slate-500">Stock: {p.stock ?? 0}</p>
              <button onClick={() => navigate(`/producto/${p.producto_id}`)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700">
                <Tag className="h-4 w-4" /> Ver producto
              </button>
            </article>
          ))}
          {productos.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-sky-200 bg-white p-6 text-center text-slate-600">
              Este comercio todavía no publicó productos.
            </div>
          )}
        </section>
      </div>

      {comercioPuntosId && (
        <button
          onClick={abrirQr}
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full border border-sky-300 bg-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-sky-700"
        >
          <QrCode className="h-4 w-4" />
          Generar QR puntos
        </button>
      )}

      {showQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">QR dinámico · {comercio.nombre}</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowQr(false)}>Cerrar</button>
            </div>
            <div className="flex justify-center rounded border border-sky-200 bg-sky-50 p-3">
              <div className="flex h-64 w-64 items-center justify-center rounded bg-white p-2">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR dinámico" className="h-full w-full rounded object-contain" />
                ) : qrActivo ? (
                  <div className="h-full w-full animate-pulse rounded bg-slate-200" />
                ) : (
                  <p className="px-4 text-center text-sm text-slate-500">Comercio inactivo para carga de puntos</p>
                )}
              </div>
            </div>
            {qrActivo && (
              <div className="rounded-md bg-sky-50 px-3 py-2 text-sm text-sky-800">
                Vigencia: <b>{segundos}s</b>. Se renueva automáticamente.
              </div>
            )}
            <p className="text-xs text-slate-500">Uso: para cargar puntos cuando el socio compra en tienda física (la compra se gestiona por fuera del catálogo).</p>
            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2" onClick={() => setShowQr(false)}>Cerrar</button>
              <button className="rounded bg-sky-700 px-3 py-2 text-white disabled:opacity-50" onClick={generarQr} disabled={!qrActivo || generandoQr}>
                {generandoQr ? 'Generando...' : 'Regenerar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
